import { collection, getDocs, query, where, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Normalize name for comparison
function normalizeName(name) {
	return name
		.toLowerCase()
		.trim()
		.replace(/\s+/g, ' ')
		.replace(/[^\w\s-']/g, ''); // Keep letters, spaces, hyphens, apostrophes
}

// Check if two names match (with variations)
function namesMatch(lessonName, studentName) {
	const normalizedLesson = normalizeName(lessonName);
	const normalizedStudent = normalizeName(studentName);

	// Exact match
	if (normalizedLesson === normalizedStudent) {
		return true;
	}

	// Check if one is contained in the other (for "FirstName LastName" vs "FirstName Middle LastName")
	if (normalizedLesson.includes(normalizedStudent) || normalizedStudent.includes(normalizedLesson)) {
		return true;
	}

	// Check reversed names (LastName, FirstName vs FirstName LastName)
	const lessonParts = normalizedLesson.split(/[\s,]+/);
	const studentParts = normalizedStudent.split(/[\s,]+/);

	if (lessonParts.length >= 2 && studentParts.length >= 2) {
		// Try matching first and last name in any order
		const lessonFirst = lessonParts[0];
		const lessonLast = lessonParts[lessonParts.length - 1];
		const studentFirst = studentParts[0];
		const studentLast = studentParts[studentParts.length - 1];

		if ((lessonFirst === studentFirst && lessonLast === studentLast) ||
		    (lessonFirst === studentLast && lessonLast === studentFirst)) {
			return true;
		}
	}

	return false;
}

export async function linkLessonsToStudents(onProgress) {
	const results = {
		total: 0,
		linked: 0,
		notMatched: 0,
		errors: [],
		matchedStudents: {},
		unmatchedNames: []
	};

	try {
		// Report progress
		if (onProgress) onProgress('Fetching students...');

		// Fetch all students
		const studentsSnapshot = await getDocs(collection(db, 'students'));
		const students = studentsSnapshot.docs.map(doc => ({
			id: doc.id,
			...doc.data()
		}));

		if (onProgress) onProgress(`Found ${students.length} students`);

		// Fetch all lessons
		if (onProgress) onProgress('Fetching lessons...');
		const lessonsSnapshot = await getDocs(collection(db, 'lessons'));
		const allLessons = lessonsSnapshot.docs.map(doc => ({
			id: doc.id,
			ref: doc.ref,
			...doc.data()
		}));

		// Filter unlinked lessons
		const unlinkedLessons = allLessons.filter(lesson => !lesson.studentId && lesson.studentName);
		results.total = unlinkedLessons.length;

		if (onProgress) onProgress(`Found ${unlinkedLessons.length} unlinked lessons`);

		if (unlinkedLessons.length === 0) {
			if (onProgress) onProgress('No unlinked lessons found!');
			return results;
		}

		// Group lessons by student name
		const lessonsByStudent = {};
		unlinkedLessons.forEach(lesson => {
			const name = lesson.studentName;
			if (!lessonsByStudent[name]) {
				lessonsByStudent[name] = [];
			}
			lessonsByStudent[name].push(lesson);
		});

		const uniqueNames = Object.keys(lessonsByStudent);
		if (onProgress) onProgress(`Processing ${uniqueNames.length} unique student names...`);

		// Match and link lessons to students
		let processedNames = 0;

		for (const [lessonName, lessons] of Object.entries(lessonsByStudent)) {
			processedNames++;

			// Find matching student
			const matchingStudent = students.find(student => {
				const studentFullName = student.fullName ||
					student.name ||
					`${student.firstName || ''} ${student.lastName || ''}`.trim();

				return namesMatch(lessonName, studentFullName);
			});

			if (matchingStudent) {
				const studentDisplayName = matchingStudent.fullName || matchingStudent.name;

				if (onProgress) {
					onProgress(`[${processedNames}/${uniqueNames.length}] Linking "${lessonName}" → "${studentDisplayName}" (${lessons.length} lessons)`);
				}

				// Update all lessons for this student
				for (const lesson of lessons) {
					try {
						await updateDoc(lesson.ref, {
							studentId: matchingStudent.id,
							linkedAt: Timestamp.now()
						});
						results.linked++;
					} catch (error) {
						console.error(`Error updating lesson ${lesson.id}:`, error);
						results.errors.push({
							lessonId: lesson.id,
							studentName: lessonName,
							error: error.message
						});
					}
				}

				results.matchedStudents[lessonName] = {
					studentId: matchingStudent.id,
					studentName: studentDisplayName,
					lessonCount: lessons.length
				};
			} else {
				if (onProgress) {
					onProgress(`[${processedNames}/${uniqueNames.length}] ✗ No match for "${lessonName}" (${lessons.length} lessons)`);
				}

				results.notMatched += lessons.length;
				results.unmatchedNames.push({
					name: lessonName,
					lessonCount: lessons.length
				});
			}
		}

		if (onProgress) onProgress('Linking complete!');

	} catch (error) {
		console.error('Error in linkLessonsToStudents:', error);
		results.errors.push({
			general: true,
			error: error.message
		});
	}

	return results;
}
